import { memo } from 'react';
import { useChartTheme } from '@/lib/charts/use-chart-theme';
import { OPACIDAD_DE_PLACA, coloresDelLogo } from '../animaciones/colores-del-logo';
import { PUNTOS } from '../animaciones/datos-del-rombo';
import { logoDePrixia } from '../animaciones/elegir-logo';
import {
  ANILLO_DE_MISION,
  OPACIDAD_DEL_CONTORNO,
  piezasDelLogo,
} from '../animaciones/piezas-del-logo';
import { POLIGONOS } from '../animaciones/placas';
import { colorDegradado } from '../animaciones/rombo';

/** Un punto apagado es su color fundido con el fondo: la misma mezcla del 74 % del motor. */
const OPACIDAD_APAGADA = 0.26;
const TAMANO_APAGADO = 0.6;
/** El cambio sin 3D: se funde, y con menos movimiento también (no hay desplazamiento). */
const TRANSICION = 'transition-opacity duration-500';

const PUNTOS_DEL_ROMBO = PUNTOS.map(([x, y, r, t], i) => ({
  cx: x,
  // En los datos `y` crece hacia arriba; en un SVG, hacia abajo.
  cy: -y,
  r,
  color: colorDegradado(t),
  anillo: ANILLO_DE_MISION[i],
}));

function LogoDeSiempre({ placas, oscuro }: { placas: number; oscuro: boolean }) {
  const colores = coloresDelLogo('clasico', oscuro ? 'oscuro' : 'claro');
  return (
    <svg viewBox="-1 3 56 58" className="h-20 w-20" aria-hidden="true">
      {POLIGONOS.map((poligono, i) => {
        const puntos = poligono.map((p) => p.join(',')).join(' ');
        const encajada = i < placas;
        return (
          <g key={i}>
            <polygon
              data-pieza={encajada ? 'encajada' : 'vacia'}
              points={puntos}
              fill={colores[i]}
              fillOpacity={OPACIDAD_DE_PLACA[i]}
              style={{ opacity: encajada ? 1 : 0 }}
              className={TRANSICION}
            />
            <polygon
              points={puntos}
              fill="none"
              stroke={colores[i]}
              strokeOpacity={OPACIDAD_DEL_CONTORNO}
              style={{ opacity: encajada ? 0 : 1 }}
              className={TRANSICION}
            />
          </g>
        );
      })}
    </svg>
  );
}

function LogoNuevo({ anillos }: { anillos: number }) {
  return (
    <svg viewBox="-0.55 -0.55 1.1 1.1" className="h-20 w-20" aria-hidden="true">
      {PUNTOS_DEL_ROMBO.map((p, i) => {
        const encendido = p.anillo < anillos;
        return (
          <circle
            key={i}
            data-encendido={encendido}
            cx={p.cx}
            cy={p.cy}
            r={encendido ? p.r : p.r * TAMANO_APAGADO}
            fill={p.color}
            style={{ opacity: encendido ? 1 : OPACIDAD_APAGADA }}
            className={TRANSICION}
          />
        );
      })}
    </svg>
  );
}

/** El logo en reposo de la tarjeta abierta: sale de las misiones hechas, sin WebGL. */
export const LogoDeMisiones = memo(function LogoDeMisiones({ hechas }: { hechas: number }) {
  const { oscuro } = useChartTheme();
  const { placas, anillos } = piezasDelLogo(hechas);
  return (
    <div className="flex justify-center pb-2" data-logo-de-misiones="">
      {logoDePrixia() === 'nuevo' ? (
        <LogoNuevo anillos={anillos} />
      ) : (
        <LogoDeSiempre placas={placas} oscuro={oscuro} />
      )}
    </div>
  );
});
